/**
 * Componente de Resultados de Busca
 */

import React from 'react'
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Button,
  Stack,
  Collapse,
  Alert,
  Tabs,
  Tab,
  alpha,
  Skeleton,
  useMediaQuery,
  useTheme
} from '@mui/material'
import DescriptionIcon from '@mui/icons-material/Description'
import PersonIcon from '@mui/icons-material/Person'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import BusinessIcon from '@mui/icons-material/Business'
import EventIcon from '@mui/icons-material/Event'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import NoteIcon from '@mui/icons-material/Note'
import SummarizeIcon from '@mui/icons-material/Summarize'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import {
  SearchResponse,
  SearchResultCategory,
  TranscriptMatch,
  EntityMatch,
  FileMatch,
  SummaryMatch,
  AnnotationMatch,
  Contradiction
} from '../types/search.types'
import { EntityType } from '../../../types/transcription'

interface SearchResultsProps {
  /** Resultados da busca */
  results: SearchResponse | null
  /** Categoria ativa */
  activeCategory: SearchResultCategory
  /** Callback para mudar categoria */
  onCategoryChange: (category: SearchResultCategory) => void
  /** Callback para abrir arquivo */
  onOpenFile: (fileId: string, segmentIndex?: number) => void
  /** Estado de loading */
  isLoading?: boolean
  /** Query usada na busca */
  query?: string
}

const ENTITY_ICONS: Record<EntityType, React.ReactNode> = {
  'PERSON': <PersonIcon sx={{ color: '#1976d2' }} />,
  'ORGANIZATION': <BusinessIcon sx={{ color: '#7b1fa2' }} />,
  'LOCATION': <LocationOnIcon sx={{ color: '#d32f2f' }} />,
  'DATE': <EventIcon sx={{ color: '#ed6c02' }} />,
  'TIME': <AccessTimeIcon sx={{ color: '#0288d1' }} />,
  'MONEY': <DescriptionIcon sx={{ color: '#2e7d32' }} />,
  'LEGAL_TERM': <DescriptionIcon sx={{ color: '#795548' }} />,
  'EVIDENCE': <DescriptionIcon sx={{ color: '#607d8b' }} />
}

// Labels em português para tipos de entidade
const ENTITY_TYPE_LABELS: Record<string, string> = {
  'PERSON': 'Pessoa',
  'ORGANIZATION': 'Organização',
  'LOCATION': 'Local',
  'DATE': 'Data',
  'TIME': 'Horário',
  'MONEY': 'Valor',
  'LEGAL_TERM': 'Termo Jurídico',
  'EVIDENCE': 'Evidência'
}

// Labels em português para tipos de match em resumos
const SUMMARY_MATCH_TYPE_LABELS: Record<string, string> = {
  'summary': 'Resumo',
  'overview': 'Visão Geral',
  'keyPoint': 'Ponto-chave',
  'keyPoints': 'Pontos-chave',
  'actionItem': 'Ação',
  'decision': 'Decisão',
  'topic': 'Tópico',
  'mainTopic': 'Tópico Principal'
}

// Labels em português para tipos de anotação
const ANNOTATION_TYPE_LABELS: Record<string, string> = {
  'bookmark': 'Favorito',
  'note': 'Nota',
  'highlight': 'Destaque'
}

// Labels em português para sentimento
const SENTIMENT_LABELS: Record<string, string> = {
  'positive': 'Positivo',
  'neutral': 'Neutro',
  'negative': 'Negativo'
}

export default function SearchResults({
  results,
  activeCategory,
  onCategoryChange,
  onOpenFile,
  isLoading = false,
  query = ''
}: SearchResultsProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // ═══════════════════════════════════════════════════════════════
  // LOADING STATE
  // ═══════════════════════════════════════════════════════════════

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2, 3].map(i => (
          <Paper key={i} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="100%" height={60} sx={{ mt: 1 }} />
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Skeleton variant="rounded" width={80} height={24} />
              <Skeleton variant="rounded" width={100} height={24} />
            </Box>
          </Paper>
        ))}
      </Box>
    )
  }

  // ═══════════════════════════════════════════════════════════════
  // EMPTY STATE
  // ═══════════════════════════════════════════════════════════════

  if (!results || results.totalResults === 0) {
    if (!query) return null

    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Nenhum resultado encontrado
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Tente usar termos diferentes ou ajustar os filtros
        </Typography>
      </Box>
    )
  }

  // ═══════════════════════════════════════════════════════════════
  // CATEGORY COUNTS
  // ═══════════════════════════════════════════════════════════════

  const counts = {
    all: results.totalResults,
    transcripts: results.results.transcriptMatches.length,
    entities: results.results.entityMatches.length,
    files: results.results.fileMatches.length,
    summaries: results.results.summaryMatches.length,
    annotations: results.results.annotationMatches.length
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER FUNCTIONS
  // ═══════════════════════════════════════════════════════════════

  const renderTranscriptMatch = (match: TranscriptMatch, index: number) => (
    <Paper
      key={`transcript-${match.fileId}-${match.segmentIndex}-${index}`}
      sx={{
        p: { xs: 1.5, sm: 2 },
        mb: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: '#1976d2',
          boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <DescriptionIcon fontSize="small" sx={{ color: '#1976d2' }} />
        <Typography
          variant="subtitle2"
          sx={{ cursor: 'pointer', '&:hover': { color: '#1976d2' } }}
          onClick={() => onOpenFile(match.fileId, match.segmentIndex)}
        >
          {match.fileName}
        </Typography>
      </Box>

      {/* Quote */}
      <Box
        sx={{
          p: 1.5,
          bgcolor: alpha('#1976d2', 0.04),
          borderRadius: 1,
          borderLeft: '3px solid #1976d2',
          mb: 1.5
        }}
      >
        <Typography
          variant="body2"
          dangerouslySetInnerHTML={{ __html: match.highlightedText }}
          sx={{
            '& mark': {
              bgcolor: '#fff59d',
              px: 0.25,
              borderRadius: 0.5
            }
          }}
        />
      </Box>

      {/* Metadata */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2 }, flexWrap: 'wrap' }}>
        <Chip
          icon={<PersonIcon sx={{ fontSize: 16 }} />}
          label={match.speaker}
          size="small"
          variant="outlined"
          sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
        />
        <Chip
          icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
          label={`${match.timestamp.start} - ${match.timestamp.end}`}
          size="small"
          variant="outlined"
          sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
        />
        {match.sentiment && (
          <Chip
            label={SENTIMENT_LABELS[match.sentiment] || match.sentiment}
            size="small"
            sx={{
              bgcolor:
                match.sentiment === 'positive'
                  ? alpha('#4caf50', 0.1)
                  : match.sentiment === 'negative'
                    ? alpha('#f44336', 0.1)
                    : alpha('#9e9e9e', 0.1),
              color:
                match.sentiment === 'positive'
                  ? '#2e7d32'
                  : match.sentiment === 'negative'
                    ? '#c62828'
                    : '#616161'
            }}
          />
        )}

        <Box sx={{ flex: 1 }} />

        <Tooltip title="Abrir no trecho">
          <IconButton
            size="small"
            onClick={() => onOpenFile(match.fileId, match.segmentIndex)}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Context preview */}
      {(match.context.before || match.context.after) && (
        <Collapse in={true}>
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #e0e0e0' }}>
            <Typography variant="caption" color="text.secondary">
              {match.context.before && (
                <span>...{match.context.before} </span>
              )}
              <strong>[trecho acima]</strong>
              {match.context.after && (
                <span> {match.context.after}...</span>
              )}
            </Typography>
          </Box>
        </Collapse>
      )}
    </Paper>
  )

  const renderEntityMatch = (match: EntityMatch, index: number) => (
    <Paper
      key={`entity-${match.entity.value}-${match.fileId}-${index}`}
      sx={{
        p: { xs: 1.5, sm: 2 },
        mb: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: '#9c27b0',
          boxShadow: '0 2px 8px rgba(156, 39, 176, 0.15)'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {ENTITY_ICONS[match.entity.type] || <DescriptionIcon />}
        <Typography variant="subtitle2" fontWeight={600}>
          {match.entity.value}
        </Typography>
        <Chip
          label={ENTITY_TYPE_LABELS[match.entity.type] || match.entity.type}
          size="small"
          sx={{ ml: 'auto', fontSize: '0.7rem', height: 20 }}
        />
      </Box>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        {match.occurrenceCount} ocorrência{match.occurrenceCount > 1 ? 's' : ''} em{' '}
        <strong>{match.fileName}</strong>
      </Typography>

      {match.occurrences.slice(0, 2).map((occ, i) => (
        <Box
          key={i}
          sx={{
            p: 1,
            bgcolor: alpha('#9c27b0', 0.04),
            borderRadius: 1,
            mt: 1
          }}
        >
          <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
            "{occ.context}"
          </Typography>
        </Box>
      ))}

      {match.occurrences.length > 2 && (
        <Button
          size="small"
          onClick={() => onOpenFile(match.fileId)}
          sx={{ mt: 1, textTransform: 'none' }}
        >
          Ver todas as {match.occurrenceCount} ocorrências
        </Button>
      )}
    </Paper>
  )

  const renderFileMatch = (match: FileMatch, index: number) => (
    <Paper
      key={`file-${match.file.fileId}-${index}`}
      onClick={() => onOpenFile(match.file.fileId)}
      sx={{
        p: { xs: 1.5, sm: 2 },
        mb: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: '#2e7d32',
          boxShadow: '0 2px 8px rgba(46, 125, 50, 0.15)'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DescriptionIcon sx={{ color: '#2e7d32' }} />
        <Typography variant="subtitle2">{match.file.displayName || match.file.title}</Typography>
        <Chip
          label={`${match.matchCount} correspondência${match.matchCount > 1 ? 's' : ''}`}
          size="small"
          sx={{ ml: 'auto' }}
        />
      </Box>
      {match.highlightedPreview && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, '& mark': { backgroundColor: '#fff3cd', fontWeight: 600, borderRadius: '2px', px: '2px' } }}
          dangerouslySetInnerHTML={{ __html: match.highlightedPreview }}
        />
      )}
    </Paper>
  )

  const renderSummaryMatch = (match: SummaryMatch, index: number) => (
    <Paper
      key={`summary-${match.fileId}-${match.matchType}-${index}`}
      sx={{
        p: { xs: 1.5, sm: 2 },
        mb: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <SummarizeIcon fontSize="small" sx={{ color: '#ff9800' }} />
        <Typography variant="subtitle2">{match.fileName}</Typography>
        <Chip
          label={SUMMARY_MATCH_TYPE_LABELS[match.matchType] || match.matchType}
          size="small"
          sx={{ ml: 'auto', bgcolor: alpha('#ff9800', 0.1) }}
        />
      </Box>
      <Typography
        variant="body2"
        dangerouslySetInnerHTML={{ __html: match.highlightedText }}
        sx={{
          '& mark': {
            bgcolor: '#fff59d',
            px: 0.25,
            borderRadius: 0.5
          }
        }}
      />
      <Button
        size="small"
        onClick={() => onOpenFile(match.fileId)}
        sx={{ mt: 1, textTransform: 'none' }}
      >
        Abrir arquivo
      </Button>
    </Paper>
  )

  const renderAnnotationMatch = (match: AnnotationMatch, index: number) => (
    <Paper
      key={`annotation-${match.fileId}-${index}`}
      sx={{
        p: { xs: 1.5, sm: 2 },
        mb: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {match.annotationType === 'bookmark' ? (
          <BookmarkIcon fontSize="small" sx={{ color: '#f44336' }} />
        ) : (
          <NoteIcon fontSize="small" sx={{ color: '#2196f3' }} />
        )}
        <Typography variant="subtitle2">{match.fileName}</Typography>
        <Chip
          label={ANNOTATION_TYPE_LABELS[match.annotationType] || match.annotationType}
          size="small"
          sx={{ ml: 'auto' }}
        />
      </Box>
      <Typography
        variant="body2"
        dangerouslySetInnerHTML={{ __html: match.highlightedText }}
        sx={{
          '& mark': {
            bgcolor: '#fff59d',
            px: 0.25,
            borderRadius: 0.5
          }
        }}
      />
    </Paper>
  )

  const renderContradiction = (contradiction: Contradiction, index: number) => (
    <Alert
      key={`contradiction-${index}`}
      severity="warning"
      icon={<WarningAmberIcon />}
      sx={{ mb: 2, borderRadius: 2 }}
    >
      <Typography variant="subtitle2" gutterBottom>
        Possível Contradição ({contradiction.type})
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
        <Box sx={{ p: 1, bgcolor: alpha('#000', 0.04), borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {contradiction.statementA.speaker} - {contradiction.statementA.fileName}
          </Typography>
          <Typography variant="body2">"{contradiction.statementA.text}"</Typography>
        </Box>
        <Box sx={{ p: 1, bgcolor: alpha('#000', 0.04), borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {contradiction.statementB.speaker} - {contradiction.statementB.fileName}
          </Typography>
          <Typography variant="body2">"{contradiction.statementB.text}"</Typography>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {contradiction.analysis}
      </Typography>
    </Alert>
  )

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <Box>
      {/* Header with stats */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {results.totalResults} resultado{results.totalResults !== 1 ? 's' : ''} encontrado
          {results.totalResults !== 1 ? 's' : ''} em {results.queryTime}ms
        </Typography>
      </Box>

      {/* Category tabs */}
      <Tabs
        value={activeCategory}
        onChange={(_, value) => onCategoryChange(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}
      >
        <Tab
          value="all"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Todos
              <Chip label={counts.all} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
            </Box>
          }
        />
        {counts.transcripts > 0 && (
          <Tab
            value="transcripts"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Trechos
                <Chip label={counts.transcripts} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
              </Box>
            }
          />
        )}
        {counts.entities > 0 && (
          <Tab
            value="entities"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Entidades
                <Chip label={counts.entities} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
              </Box>
            }
          />
        )}
        {counts.files > 0 && (
          <Tab
            value="files"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Arquivos
                <Chip label={counts.files} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
              </Box>
            }
          />
        )}
        {counts.summaries > 0 && (
          <Tab
            value="summaries"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Resumos
                <Chip label={counts.summaries} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
              </Box>
            }
          />
        )}
        {counts.annotations > 0 && (
          <Tab
            value="annotations"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Anotações
                <Chip label={counts.annotations} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
              </Box>
            }
          />
        )}
      </Tabs>

      {/* Contradictions alert */}
      {results.insights.contradictions.length > 0 && activeCategory === 'all' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon color="warning" fontSize="small" />
            Possíveis Contradições Detectadas
          </Typography>
          {results.insights.contradictions.map((c, i) => renderContradiction(c, i))}
        </Box>
      )}

      {/* Results list */}
      <Box>
        {(activeCategory === 'all' || activeCategory === 'transcripts') &&
          results.results.transcriptMatches.map((m, i) => renderTranscriptMatch(m, i))}

        {(activeCategory === 'all' || activeCategory === 'entities') &&
          results.results.entityMatches.map((m, i) => renderEntityMatch(m, i))}

        {(activeCategory === 'all' || activeCategory === 'files') &&
          results.results.fileMatches.map((m, i) => renderFileMatch(m, i))}

        {(activeCategory === 'all' || activeCategory === 'summaries') &&
          results.results.summaryMatches.map((m, i) => renderSummaryMatch(m, i))}

        {(activeCategory === 'all' || activeCategory === 'annotations') &&
          results.results.annotationMatches.map((m, i) => renderAnnotationMatch(m, i))}
      </Box>

      {/* Top people insight */}
      {results.insights.topPeople.length > 0 && activeCategory === 'all' && (
        <Paper sx={{ p: 2, mt: 2, borderRadius: 2, bgcolor: alpha('#1976d2', 0.04) }}>
          <Typography variant="subtitle2" gutterBottom>
            Pessoas mais mencionadas nos resultados
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {results.insights.topPeople.map((person, i) => (
              <Chip
                key={i}
                icon={<PersonIcon />}
                label={`${person.name} (${person.count})`}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  )
}
